import { watch, computed, onMounted, ref, InjectionKey, Ref, provide, inject } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useMutation } from '@/helpers/composables/useApollo';
import { useConstantStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { Platform } from '@/@types/graphql-types';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { SwiperRule } from '@/helpers/promotion-helper';
import {
	GetPlacementDocument,
	GetPlacementMutation,
	GetPlacementMutationVariables,
} from '@/graphql/mutation/GetPlacement.mutation';
import adminConfig from '@/features/promotions/config.json';
import { SnowplowFlexContext, SnowplowContext } from '@/helpers/tracking/providers';

interface PromotionTrackingInput {
	placement: MaybeRefOrGetter<'sqtv' | 'qtv' | 'swiper' | 'ft' | null>;
	providers: MaybeRefOrGetter<(string | null | undefined)[] | SwiperRule>;
	enabled: MaybeRefOrGetter<boolean>;
	titleSourceId: string | null;
}

interface AdServerAdContent {
	provider?: string;
	presentation?: string;
}

type AdServerRenderAction = 'enabled' | 'disabled' | 'mismatch' | 'invalid_response' | 'override';

export interface AdServerIds {
	adId: number | null;
	bidId: string | null;
	campaignId: number | null;
}

export const AD_SERVER_IDS_KEY = Symbol() as InjectionKey<Ref<AdServerIds | null>>;

export function useAdServer(props: PromotionTrackingInput) {
	const route = useRoute();
	const { country } = useLanguageStore();
	const { isProUser } = useUserStore();

	const { allProvidersByShortName } = useConstantStore();

	const activeCountries: string[] = adminConfig?.ad_server?.countries ?? [];

	const placement = computed(() => toValue(props.placement));
	const providers = computed(() => toValue(props.providers));
	const enabled = computed(() => toValue(props.enabled));
	const isActiveCountry = computed(() => {
		return activeCountries.includes(country.value);
	});

	/* MUTATION  */
	const expectedResult = computed(() => {
		if (isProUser.value || placement.value == null) return null;

		return {
			placement: placement.value,
			providers: providers.value,
		};
	});

	const mutationVariables = computed(() => ({
		country: country.value,
		expectedResult: JSON.stringify(expectedResult.value),
		limit: 1,
		placements: ['buybox'],
		platform: Platform.Web,
		url: route.path,
		jwEntityID: props.titleSourceId,
	}));

	const { mutate, onError } = useMutation<GetPlacementMutation, GetPlacementMutationVariables>(GetPlacementDocument);

	const adServerResponse = ref<GetPlacementMutation | null>(null);

	onError((error: any) => {
		captureMessageForSentry(
			'[GetPlacement Mutation]:',
			{
				error,
				where: 'useAdServer.ts mutation',
				promotion: {
					placement: placement.value,
					providers: providers.value,
				},
			},
			'error'
		);
	});

	/* Extract ad IDs from response */
	const adServerIds = computed<AdServerIds>(() => {
		if (!adServerResponse.value?.getPlacement) {
			return { adId: null, bidId: null, campaignId: null };
		}

		const placement = adServerResponse.value.getPlacement;
		const ad = placement.ads?.[0];

		return {
			adId: ad?.adID ?? null,
			bidId: placement.bidID ?? null,
			campaignId: ad?.campaignID ?? null,
		};
	});
	provide(AD_SERVER_IDS_KEY, adServerIds);

	/* VALIDATE AND PROCESS RESPONSE */
	/* Parse the stringified adContent from ad server response */
	const parsedAdServerResponse = computed(() => {
		if (!adServerResponse.value || !isActiveCountry.value) return null;
		// Explicit content warning: the adServer is currently returning a stringified object
		// we need to valiate that the returned object is a legitimate promotion (presentation) and provider
		// we will need a type safe return before a rollout of adServer to other regions is possible
		const adContent = adServerResponse.value?.getPlacement.ads?.[0].adContent;

		return adContent ? (JSON.parse(adContent) as AdServerAdContent) : null;
	});

	/* Validate that the parsed content contains valid provider and presentation values */
	const isValidObjectFromAdServer = computed(() => {
		const content = parsedAdServerResponse.value;
		if (!content) return false;

		const { provider, presentation } = content;
		if (!provider || !presentation) return false;

		return isValidAdServerPlacement(presentation) && !!allProvidersByShortName.value[provider];
	});

	/* Return promotion only if valid and country is active */
	const promotion = computed(() => {
		if (!adServerResponse || !isActiveCountry.value) return null;

		if (!parsedAdServerResponse.value) return null;
		if (!isValidObjectFromAdServer.value) return null;

		return parsedAdServerResponse.value;
	});

	/* Extract placement from validated promotion */
	const placementFromAdServer = computed(() => {
		if (!promotion.value || !isValidAdServerPlacement(promotion.value.presentation)) return null;
		return promotion.value.presentation;
	});

	/* Extract provider shortName from validated promotion */
	const shortNameFromAdServer = computed(() => promotion.value?.provider ?? null);

	/* Determine render action based on validation state */
	const adServerRenderAction = computed(() => {
		if (!isActiveCountry.value) return 'disabled';
		// promotion is null case ignored as this will never be attached to offer impression/clickout
		if (!isValidObjectFromAdServer.value) return 'invalid_response';
		return 'enabled';
	});

	const hasPromotionFromAdServer = computed(() => !!promotion.value && adServerRenderAction.value === 'enabled');

	/*
	 * Server
	 * call the promotion mutation once on server, to confirm expectedResult in SSR
	 */
	if (process.server) {
		if (enabled.value) {
			callAdServerMutation();
		} else {
			const stopWatcher = watch(enabled, () => {
				if (enabled.value) {
					callAdServerMutation();
					stopWatcher();
				}
			});
		}
	}

	/*
	 * Client
	 * calls the promotion mutation on client
	 */
	onMounted(() => {
		callAdServerMutation();
	});

	function callAdServerMutation() {
		mutate({
			input: mutationVariables.value,
		}).then(res => {
			if (!res?.data) return;
			adServerResponse.value = res.data;
		});
	}

	function isValidAdServerPlacement(presentation: string | null | undefined): presentation is 'ft' | 'sqtv' | 'qtv' {
		return ['ft', 'sqtv', 'qtv'].includes(presentation || '');
	}

	return {
		hasPromotionFromAdServer,
		placementFromAdServer,
		shortNameFromAdServer,
		promotion,
		isValidAdServerPlacement,
		adServerRenderAction,
		adServerIds,
	};
}

export function useInjectAdServerIds() {
	return inject(AD_SERVER_IDS_KEY, ref<AdServerIds | null>(null));
}

export function getAdServerContexts(renderAction: AdServerRenderAction) {
	return new SnowplowFlexContext({ data: { ad_server: renderAction } });
}

export function attachAdServerContexts(additionalContexts: SnowplowContext[] = [], renderAction: AdServerRenderAction) {
	return [...additionalContexts, getAdServerContexts(renderAction)];
}
